import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Layout from './components/layout/Layout';
import LoadingState from './components/ui/LoadingState';
import Home from './pages/Home';
import { clearStaleChunkReloadFlag } from './lib/chunkReload';

// Public guest pages are lazy-loaded per-route so a first visit only
// downloads the one page it needs; the admin section (below) is lazy as a
// whole so guest visitors never download admin-only code (charts, booking
// management, etc.) at all. Home is the exception — it's a static import so
// its hero-data fetch fires the instant the app mounts instead of waiting on
// a lazy chunk to download first, since it's where nearly every visit lands.
const Rooms = lazy(() => import('./pages/Rooms'));
const RoomDetail = lazy(() => import('./pages/RoomDetail'));
const Spaces = lazy(() => import('./pages/Spaces'));
const Reviews = lazy(() => import('./pages/Reviews'));
const Location = lazy(() => import('./pages/Location'));
const TouristAttractions = lazy(() => import('./pages/TouristAttractions'));
const ThingsToDoInGujarat = lazy(() => import('./pages/ThingsToDoInGujarat'));
const AhmedabadDayPlanner = lazy(() => import('./pages/AhmedabadDayPlanner'));
const NearbyShopsAndRestaurants = lazy(() => import('./pages/NearbyShopsAndRestaurants'));
const About = lazy(() => import('./pages/About'));
const Sustainability = lazy(() => import('./pages/Sustainability'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Contact = lazy(() => import('./pages/Contact'));
const CheckIn = lazy(() => import('./pages/CheckIn'));
const CancellationPolicy = lazy(() => import('./pages/CancellationPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Sitemap = lazy(() => import('./pages/Sitemap'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const CustomPage = lazy(() => import('./pages/CustomPage'));
const NotFound = lazy(() => import('./pages/NotFound'));

const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminBookings = lazy(() => import('./pages/admin/AdminBookings'));
const AdminCalendar = lazy(() => import('./pages/admin/AdminCalendar'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminInfoGuide = lazy(() => import('./pages/admin/AdminInfoGuide'));
const AdminStats = lazy(() => import('./pages/admin/AdminStats'));
const AdminEmailLogs = lazy(() => import('./pages/admin/AdminEmailLogs'));
const AdminMessages = lazy(() => import('./pages/admin/AdminMessages'));
const AdminRedGreen = lazy(() => import('./pages/admin/AdminRedGreen'));
const AdminMedia = lazy(() => import('./pages/admin/AdminMedia'));
const AdminHeroSlides = lazy(() => import('./pages/admin/AdminHeroSlides'));
const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));
const AdminRooms = lazy(() => import('./pages/admin/AdminRooms'));
const AdminSpaces = lazy(() => import('./pages/admin/AdminSpaces'));
const AdminPageEditor = lazy(() => import('./pages/admin/AdminPageEditor'));
const AdminFAQ = lazy(() => import('./pages/admin/AdminFAQ'));
const AdminShopsAndRestaurants = lazy(() => import('./pages/admin/AdminShopsAndRestaurants'));
const AdminSiteContent = lazy(() => import('./pages/admin/AdminSiteContent'));
const AdminPages = lazy(() => import('./pages/admin/AdminPages'));
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'));
const AdminSEO = lazy(() => import('./pages/admin/AdminSEO'));
const AdminPageSections = lazy(() => import('./pages/admin/AdminPageSections'));

function RouteFallback() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <LoadingState label="Loading…" />
    </div>
  );
}

// Renders as a child of ChunkErrorBoundary, so mounting/updating at all
// (i.e. this effect running) proves the current route's chunk loaded fine
// — safe to clear the stale-chunk reload guard so a *later, unrelated*
// stale chunk (e.g. after the next deploy) still gets its own single
// auto-reload instead of jumping straight to the manual "please refresh"
// screen. See lib/chunkReload.ts.
function ChunkReloadGuardReset() {
  const location = useLocation();
  useEffect(() => {
    clearStaleChunkReloadFlag();
  }, [location.pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ChunkReloadGuardReset />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="rooms" element={<Rooms />} />
            <Route path="rooms/:slug" element={<RoomDetail />} />
            <Route path="spaces" element={<Spaces />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="location" element={<Location />} />
            <Route path="tourist-attractions" element={<TouristAttractions />} />
            <Route path="things-to-do-in-gujarat" element={<ThingsToDoInGujarat />} />
            <Route path="ahmedabad-tourist-place-planner-day-wise" element={<AhmedabadDayPlanner />} />
            <Route path="near-by-shop-and-restaurant" element={<NearbyShopsAndRestaurants />} />
            <Route path="about" element={<About />} />
            <Route path="sustainability" element={<Sustainability />} />
            <Route path="faq" element={<FAQ />} />
            <Route path="contact" element={<Contact />} />
            <Route path="checkin" element={<CheckIn />} />
            <Route path="cancellation-policy" element={<CancellationPolicy />} />
            <Route path="terms-conditions" element={<TermsConditions />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="sitemap" element={<Sitemap />} />
            <Route path="blog" element={<Blog />} />
            <Route path="blog/:slug" element={<BlogPost />} />
            <Route path=":slug" element={<CustomPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="admin/login" element={<AdminLogin />} />
          <Route path="admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="calendar" element={<AdminCalendar />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="email-logs" element={<AdminEmailLogs />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="red-green" element={<AdminRedGreen />} />
            <Route path="media" element={<AdminMedia />} />
            <Route path="hero-slides" element={<AdminHeroSlides />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="page-editor" element={<AdminPageEditor />} />
            <Route path="rooms" element={<AdminRooms />} />
            <Route path="spaces" element={<AdminSpaces />} />
            <Route path="faq" element={<AdminFAQ />} />
            <Route path="shops-restaurants" element={<AdminShopsAndRestaurants />} />
            <Route path="site-content" element={<AdminSiteContent />} />
            <Route path="pages" element={<AdminPages />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="seo" element={<AdminSEO />} />
            <Route path="page-sections" element={<AdminPageSections />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="settings/info" element={<AdminInfoGuide />} />
            <Route path="settings/stats" element={<AdminStats />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
